import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MANDATE_ACTION_LABELS,
  isMandateTerminal,
  type Mandate,
  type MandateAction,
  type MandateTransition,
} from '@aimmo/contracts';
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
  resourceKeys,
} from '@aimmo/ui';
import { api, assertOk } from '../lib/api.js';
import { invalidateActivityViews } from '../lib/activities.js';
import { today } from '../lib/dates.js';
import { errorMessage } from '../lib/errors.js';

/**
 * Two of the six transitions carry information: closing records the outcome,
 * renewing sets the new expiry. The rest are a single click.
 */
const NEEDS_INPUT: Partial<Record<MandateAction, 'close' | 'renew'>> = {
  close: 'close',
  renew: 'renew',
};

export interface MandateTransitionsProps {
  mandate: Mandate;
  /** False hides the controls entirely — the API would refuse them anyway. */
  allowed: boolean;
}

/**
 * The mandate's lifecycle controls (docs/domain/06-mandates.md).
 *
 * The buttons come from `availableActions`, which the API resolves from the same
 * transition table it enforces with. Nothing here decides what is legal — a
 * screen that made that judgement itself would eventually offer a button that
 * produces a 422.
 */
export function MandateTransitions({ mandate, allowed }: MandateTransitionsProps) {
  const queryClient = useQueryClient();
  const [pending, setPending] = React.useState<MandateAction | null>(null);
  const [closedPrice, setClosedPrice] = React.useState('');
  const [expiresAt, setExpiresAt] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const transition = useMutation({
    mutationFn: async (body: MandateTransition) =>
      assertOk(await api.mandates.transition({ params: { id: mandate.id }, body })).body,
    onSuccess: () => {
      setPending(null);
      setError(null);
      // A transition reaches further than its own row: the property's status
      // follows a closure, the owner's derived badges follow the mandate, and
      // the system writes a status_change onto the timeline below.
      void queryClient.invalidateQueries({ queryKey: resourceKeys.all('mandates') });
      void queryClient.invalidateQueries({ queryKey: resourceKeys.all('properties') });
      void queryClient.invalidateQueries({ queryKey: resourceKeys.all('contacts') });
      void invalidateActivityViews(queryClient);
    },
    onError: (cause) => {
      setError(errorMessage(cause, 'Operazione non riuscita.'));
    },
  });

  const run = (action: MandateAction) => {
    if (NEEDS_INPUT[action]) {
      setPending(action);
      setClosedPrice(mandate.price ?? '');
      setExpiresAt(mandate.expiresAt ?? today());
      return;
    }
    transition.mutate({ action } as MandateTransition);
  };

  const confirm = () => {
    if (pending === 'close') {
      transition.mutate({
        action: 'close',
        closedAt: today(),
        ...(closedPrice ? { closedPrice } : {}),
      });
    }
    if (pending === 'renew' && expiresAt) {
      transition.mutate({ action: 'renew', expiresAt });
    }
  };

  // Closed is a property of the state, not of an empty button row: a mandate
  // can offer nothing to someone without the permission and still be open.
  if (isMandateTerminal(mandate.status)) {
    return (
      <p className="text-secondary text-muted">Nessuna azione disponibile: l’incarico è chiuso.</p>
    );
  }
  if (!allowed) {
    return (
      <p className="text-secondary text-muted">
        Non hai i permessi per cambiare lo stato di questo incarico.
      </p>
    );
  }

  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap gap-2">
        {mandate.availableActions.map((action) => (
          <Button
            key={action}
            type="button"
            variant={action === 'withdraw' ? 'ghost' : 'secondary'}
            size="sm"
            disabled={transition.isPending}
            onClick={() => run(action as MandateAction)}
          >
            {transition.isPending && <Spinner className="text-current" />}
            {MANDATE_ACTION_LABELS[action as MandateAction]}
          </Button>
        ))}
      </div>

      {error && (
        <Alert tone="danger">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Dialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pending === 'close' ? 'Chiudi l’incarico' : 'Rinnova l’incarico'}
            </DialogTitle>
          </DialogHeader>

          {pending === 'close' ? (
            <div className="grid gap-2">
              <Label htmlFor="closed-price">Prezzo finale (€)</Label>
              <Input
                id="closed-price"
                value={closedPrice}
                placeholder="280000.00"
                onChange={(event) => setClosedPrice(event.target.value)}
              />
              <p className="text-secondary text-muted">
                Chiudendo, l’immobile passa a{' '}
                {mandate.contractType === 'rent' ? 'affittato' : 'venduto'}.
              </p>
            </div>
          ) : (
            <div className="grid gap-2">
              <Label htmlFor="expires-at">Nuova scadenza</Label>
              <Input
                id="expires-at"
                type="date"
                value={expiresAt}
                onChange={(event) => setExpiresAt(event.target.value)}
              />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setPending(null)}>
              Annulla
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={transition.isPending || (pending === 'renew' && !expiresAt)}
              onClick={confirm}
            >
              Conferma
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
